// EditContactSheet — opens on a contact row click; allows patching the
// identity fields + delete. Linked-applications list is read-only here
// (link mutations live on the per-application Sheet, which is the ergonomic
// home for them).

import { useEffect, useState } from "react";
import { Sheet } from "../ui/Sheet";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { Select } from "../ui/Select";
import { Pill } from "../ui/Pill";
import { Spinner } from "../ui/Spinner";
import { useCompanies } from "../../lib/companies";
import {
  useContact,
  useDeleteContact,
  usePatchContact,
  type ContactRow,
} from "../../lib/contacts";
import { MessageDraftDialog } from "./MessageDraftDialog";

const OTHER_VALUE = "__other__";

export function EditContactSheet({
  open,
  onOpenChange,
  row,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ContactRow | null;
}) {
  const detail = useContact(row?.id ?? null);
  const patch = usePatchContact(row?.id ?? "");
  const del = useDeleteContact();

  const companies = useCompanies();
  const [fullName, setFullName] = useState("");
  const [companyId, setCompanyId] = useState<string>(OTHER_VALUE);
  const [role, setRole] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!row) return;
    setFullName(row.full_name);
    setCompanyId(row.company_id ?? OTHER_VALUE);
    setRole(row.role ?? "");
    setLinkedinUrl(row.linkedin_url ?? "");
    setEmail(row.email ?? "");
    setNotes(row.notes ?? "");
  }, [row]);

  if (!row) return null;

  function save() {
    patch.mutate(
      {
        full_name: fullName.trim() || row!.full_name,
        company_id: companyId === OTHER_VALUE ? null : companyId,
        role: role.trim() || null,
        linkedin_url: linkedinUrl.trim() || null,
        email: email.trim() || null,
        notes: notes.trim() || null,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  function remove() {
    if (!row) return;
    if (!confirm(`Delete ${row.full_name}? Linked applications keep their reference broken.`)) {
      return;
    }
    del.mutate({ id: row.id }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <Sheet.Content>
        <Sheet.Header
          title={row.full_name}
          description={row.company_name ?? "Unaffiliated / unknown company"}
          eyebrow="Contact"
        />
        <Sheet.Body className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-neutral-500">
                Full name
              </label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-neutral-500">
                Company
              </label>
              <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <option value={OTHER_VALUE}>Other / unknown</option>
                {(companies.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-neutral-500">
                Role at their company
              </label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-neutral-500">
                Email
              </label>
              <Input value={email} type="email" onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              LinkedIn URL
            </label>
            <Input value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-neutral-500">
              Notes
            </label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
          </div>

          <div className="rounded-md border border-neutral-800 p-3">
            <p className="text-xs uppercase tracking-wider text-neutral-500">
              Linked applications
            </p>
            <div className="mt-2 space-y-2">
              {detail.isLoading ? (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <Spinner size={14} /> Loading…
                </div>
              ) : (detail.data?.links ?? []).length === 0 ? (
                <p className="text-xs text-neutral-500">
                  Not linked to any application yet. Use the application Sheet's
                  "Add contact" flow to attach this contact to an opening.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {(detail.data?.links ?? []).map((link) => (
                    <li
                      key={link.application_id}
                      className="flex items-center justify-between gap-3 text-sm text-neutral-200"
                    >
                      <span className="truncate">
                        {link.role}
                        {link.role_in_process ? (
                          <span className="ml-2 text-xs text-neutral-500">
                            ({link.role_in_process})
                          </span>
                        ) : null}
                      </span>
                      <Pill tone={statusTone(link.status)}>{link.status}</Pill>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Sheet.Body>
        <Sheet.Footer>
          <Button variant="danger" onClick={remove} disabled={del.isPending}>
            {del.isPending ? "Deleting…" : "Delete"}
          </Button>
          <MessageDraftDialog
            contact={row}
            trigger={<Button variant="secondary">Draft message</Button>}
          />
          <Button onClick={save} disabled={patch.isPending}>
            {patch.isPending ? "Saving…" : "Save"}
          </Button>
        </Sheet.Footer>
      </Sheet.Content>
    </Sheet>
  );
}

function statusTone(status: string): "neutral" | "warning" | "success" | "info" {
  if (status === "interviewing" || status === "offer") return "success";
  if (status === "rejected" || status === "withdrawn") return "neutral";
  if (status === "applied") return "info";
  return "warning";
}
